import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  FillStyles,
  IncidentStyleMap,
  incidentTypes,
  StyleMap,
} from "../types";
import { ExpressionSpecification } from "mapbox-gl";

export const incidentMarkerMinimumPixelSize = 28;
export const incidentMarkerLayerId = "incident-marker-layer";

export const additionalColours = {
  DEFORESTATION: "#2e9a2e",
  EARTHQUAKE: "#892806",
  TSUNAMI: "#fcf7f7",
};

export const earthlyBrandColours = {
  EARTHLY: "#40e4ab",
  SAND: "#D9CA80",
  SUN: "#E35E40",
  SUN50: "#eba899",
  CARBON: "#797979",
  SEA: "#3062C0",
  SEA50: "#91aad9",
  SKY: "#76BEC6",
  PLAIN: "#ffffff",
};

const incidentStyleMapper = {
  FIRE: {
    id: "fire",
    colour: earthlyBrandColours.SUN,
    opacity: 0.4,
    background: "#fbefec",
  },
  DEFORESTATION: {
    id: "deforestation",
    colour: additionalColours.DEFORESTATION,
    opacity: 0.4,
    background: "#edf9ed",
  },
  DROUGHT: {
    id: "drought",
    colour: earthlyBrandColours.SAND,
    opacity: 0.4,
    background: "#faf8ef",
  },
  FLOOD: {
    id: "flood",
    colour: earthlyBrandColours.SEA,
    opacity: 0.4,
    background: "#eef3fb",
  },
  EARTHQUAKE: {
    id: "earthquake",
    colour: additionalColours.EARTHQUAKE,
    opacity: 0.4,
    background: "#f6eeeb",
  },
  "TROPICAL CYCLONE": {
    id: "tropical-cyclone",
    colour: earthlyBrandColours.SKY,
    opacity: 0.4,
    background: "#f1fafb",
  },
  VOLCANO: {
    id: "volcano",
    colour: earthlyBrandColours.CARBON,
    opacity: 0.4,
    background: "#f4f4f4",
  },
  TSUNAMI: {
    id: "tsunami",
    colour: additionalColours.TSUNAMI,
    opacity: 0.4,
    background: "#fafafa",
  },
} satisfies IncidentStyleMap;

export const styleMapper = {
  outline: {
    id: "outline",
    colour: earthlyBrandColours.EARTHLY,
    opacity: 0.4,
    background: "#effcf7",
  },
  buffer: {
    id: "buffer",
    colour: earthlyBrandColours.EARTHLY,
    opacity: 0.2,
    background: "#effcf7",
  },
  FallbackOption: {
    id: "fallback",
    colour: earthlyBrandColours.PLAIN,
    opacity: 0.4,
    background: "#fafafa",
  },
  ...incidentStyleMapper,
} satisfies StyleMap;

export const createFillAppearance = (style: FillStyles) => {
  const { id, colour, opacity } = style;

  return {
    id: `${id}-fill`,
    type: "fill" as const,
    paint: {
      "fill-color": colour,
      "fill-opacity": opacity,
    },
  };
};

export const createLineAppearance = (
  id: string,
  colour: string,
  width: number,
) => ({
  id,
  type: "line" as const,
  paint: {
    "line-color": colour,
    "line-width": width,
  },
});

export const Title = styled(Typography)({
  fontSize: "13px",
  fontWeight: 700,
  paddingTop: 4,
});

export const interactiveLayerIds = [
  `${styleMapper.buffer.id}-fill`,
  `${styleMapper.outline.id}-fill`,
  ...incidentTypes.map(
    (incidentType) => `${styleMapper[incidentType].id}-fill`,
  ),
  incidentMarkerLayerId,
];

export const createIncidentMarkerColour = (): ExpressionSpecification => [
  "match",
  ["get", "incident"],
  ...incidentTypes.flatMap((incidentType) => [
    incidentType,
    styleMapper[incidentType].colour,
  ]),
  earthlyBrandColours.EARTHLY,
];
